"use client";

/**
 * ModuleBuilderPage.jsx — FULL REPLACEMENT
 *
 * Changes vs previous version:
 *
 *   1. Back button: navigates to the project's piecework tab properly via
 *      location.state.returnTo or falls back to navigate(-1).
 *
 *   2. Material removed entirely: items are now pure labor rows.
 *      - UOM picker: EA | LF | SF drives dimension mode.
 *      - LF → default length input (mixed-feet parser, stored as inches).
 *      - SF → default H and L inputs (mixed-feet parser, stored as feet).
 *      - EA → no dimension fields.
 *      Coverage shown to workers = qty × length (LF) or H × L × qty (SF).
 *
 *   3. Multiple work orders: when creating a new module the WO picker is
 *      a multi-select checklist; saving POSTs one module per selected WO
 *      (or one WO-less module if none selected).
 *
 *   4. Worker-facing section label: a separate "Display Name" field that
 *      overrides the cost-code name shown to workers without changing the
 *      accounting code.
 *
 *   5. Burden division: rates fetched from ItemDatabase already include
 *      burden. We divide by (1 + burdenPct/100) so the stored unitRate
 *      is the pre-burden worker rate. Burden is re-applied server-side
 *      during confirmation.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import LaborSearch, { invalidateLaborCache } from "./LaborSearch";
const API = "https://erp-xe9u.onrender.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

const UOM_OPTIONS = ["EA", "LF", "SF"];

const getDimMode = (uom) => {
  const u = (uom || "").toUpperCase();
  if (u === "SF") return "sf";
  if (u === "LF") return "lf";
  return "qty";
};

// ── Dimension / size parsing ───────────────────────────────────────────────
const parseFraction = (s) => {
  const m = s.trim().match(/^(\d+)\/(\d+)$/);
  if (m) return parseInt(m[1], 10) / parseInt(m[2], 10);
  return null;
};

const parseMixedFeet = (s) => {
  if (!s || !s.trim()) return null;
  s = s.trim();
  let m = s.match(/^(\d+(?:\.\d+)?)\s*'?\s*(\d+)[-\s](\d+\/\d+)"?$/);
  if (m)
    return (
      parseFloat(m[1]) + (parseInt(m[2], 10) + (parseFraction(m[3]) ?? 0)) / 12
    );
  m = s.match(/^(\d+(?:\.\d+)?)\s*'?\s*(\d+(?:\.\d+?)?)"$/);
  if (m) return parseFloat(m[1]) + parseFloat(m[2]) / 12;
  m = s.match(/^(\d+)-(\d+\/\d+)"?$/);
  if (m) return (parseInt(m[1], 10) + (parseFraction(m[2]) ?? 0)) / 12;
  m = s.match(/^(\d+(?:\.\d+?)?)\s*'$/);
  if (m) return parseFloat(m[1]);
  m = s.match(/^(\d+(?:\.\d+?)?)"$/);
  if (m) return parseFloat(m[1]) / 12;
  m = s.match(/^(\d+(?:\.\d+?)?)$/);
  if (m) return parseFloat(m[1]);
  return null;
};

const parseInches = (s) => {
  if (!s || !s.trim()) return null;
  s = s.trim();
  let m = s.match(/^(\d+(?:\.\d+)?)\s*'\s*(\d+)[-](\d+\/\d+)"?$/);
  if (m)
    return (
      parseFloat(m[1]) * 12 + parseInt(m[2], 10) + (parseFraction(m[3]) ?? 0)
    );
  m = s.match(/^(\d+(?:\.\d+)?)\s*'\s*(\d+(?:\.\d+?)?)"?$/);
  if (m) return parseFloat(m[1]) * 12 + parseFloat(m[2]);
  m = s.match(/^(\d+(?:\.\d+)?)\s*'$/);
  if (m) return parseFloat(m[1]) * 12;
  m = s.match(/^(\d+)-(\d+\/\d+)"?$/);
  if (m) return parseInt(m[1], 10) + (parseFraction(m[2]) ?? 0);
  m = s.match(/^(\d+(?:\.\d+?)?)"?$/);
  if (m) return parseFloat(m[1]);
  return null;
};

const inchesToFeet = (inches) => (inches != null ? inches / 12 : 0);

const emptyLabor = () => ({
  itemCode: "",
  description: "",
  uom: "EA",
  unitRate: 0,
  _rateInput: "",
  defaultQty: "",
  defaultLengthInches: 0,
  defaultLengthInput: "",
  defaultHeightFt: 0,
  defaultLengthFt: 0,
  defaultHeightInput: "",
  defaultLengthFtInput: "",
});

const newItem = (overrides = {}) => ({
  _localId: Math.random().toString(36).slice(2),
  labor: emptyLabor(),
  suggestedQty: 0,
  isActive: true,
  sortOrder: 0,
  _collapsed: false,
  ...overrides,
});

// ── Icons ──────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 16, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
  >
    <path fillRule="evenodd" clipRule="evenodd" d={d} />
  </svg>
);
const ICONS = {
  chevronDown:
    "M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z",
  x: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z",
  search:
    "M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z",
  copy: "M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z",
  sliders:
    "M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 6a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 6a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z",
  plus: "M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z",
  trash:
    "M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z",
  layers:
    "M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V6zm0 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2z",
  check:
    "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z",
};

const Spinner = ({ size = 16 }) => (
  <div
    className="border-2 border-orange-500 border-t-transparent rounded-full animate-spin shrink-0"
    style={{ width: size, height: size }}
  />
);

// ── CostCodeSelector ───────────────────────────────────────────────────────
function CostCodeSelector({ token, value, onChange }) {
  const [costCodes, setCostCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/company-profile/cost-codes`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) =>
        setCostCodes((r.data || []).filter((c) => c.active !== false)),
      )
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return costCodes;
    return costCodes.filter(
      (c) =>
        c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q),
    );
  }, [costCodes, query]);

  const selected = value?.id
    ? (costCodes.find((c) => String(c._id) === String(value.id)) ?? null)
    : null;

  if (selected && !expanded) {
    return (
      <div className="flex items-center gap-2">
        <span className="px-2.5 py-1 bg-orange-500/20 border border-orange-500/50 text-orange-200 rounded-lg text-[11px] font-mono font-medium">
          {selected.code}
        </span>
        <span className="text-sm text-slate-100 font-medium">
          {selected.name}
        </span>
        <button
          onClick={() => setExpanded(true)}
          className="ml-2 text-[11px] text-slate-400 hover:text-slate-100 border border-slate-600 hover:border-slate-400 px-2 py-0.5 rounded-md transition-colors"
        >
          Change
        </button>
        <button
          onClick={() => onChange({ id: null, code: "", name: "" })}
          className="text-slate-500 hover:text-rose-400 transition-colors"
        >
          <Icon d={ICONS.x} size={12} />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg focus-within:border-orange-500 transition-colors">
        <Icon d={ICONS.search} size={13} className="text-slate-400 shrink-0" />
        <input
          autoFocus={expanded}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            loading
              ? "Loading cost codes…"
              : `Filter ${costCodes.length} section${costCodes.length !== 1 ? "s" : ""}…`
          }
          disabled={loading}
          className="flex-1 text-xs bg-transparent text-slate-100 placeholder-slate-500 focus:outline-none disabled:opacity-50"
        />
        {loading && <Spinner size={13} />}
        {query && !loading && (
          <button
            onClick={() => setQuery("")}
            className="text-slate-500 hover:text-slate-300 text-base leading-none"
          >
            ×
          </button>
        )}
        {expanded && selected && (
          <button
            onClick={() => {
              setExpanded(false);
              setQuery("");
            }}
            className="text-[11px] text-slate-400 hover:text-slate-200 ml-1 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>

      {!loading && (
        <div className="flex flex-wrap gap-1.5">
          {filtered.map((c) => {
            const isSelected = String(c._id) === String(value?.id);
            return (
              <button
                key={String(c._id)}
                onClick={() => {
                  onChange(
                    isSelected
                      ? { id: null, code: "", name: "" }
                      : { id: c._id, code: c.code, name: c.name },
                  );
                  if (!isSelected) setExpanded(false);
                  setQuery("");
                }}
                className={cls(
                  "px-2.5 py-1 text-[11px] font-medium rounded-lg border transition-all text-left",
                  isSelected
                    ? "border-orange-500 bg-orange-500/20 text-orange-200 shadow-sm"
                    : "border-slate-600 text-slate-300 hover:border-slate-400 hover:text-white bg-slate-800/60",
                )}
              >
                <span className="font-mono text-[10px] mr-1.5 opacity-80">
                  {c.code}
                </span>
                {c.name}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-xs text-slate-500 py-1">
              {query
                ? `No sections match "${query}"`
                : "No cost codes configured — add them in Company Settings."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Work Order Multi-Select ────────────────────────────────────────────────
function WorkOrderMultiSelect({ workOrders, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);

  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const selected = workOrders.filter((w) => selectedIds.includes(w._id));

  return (
    <div className="space-y-2">
      {/* ── Trigger button ── */}
      <div className="relative">
        <button
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:border-slate-500 focus:border-orange-500 focus:outline-none transition-colors"
        >
          <span className="truncate text-slate-400">
            {selected.length === 0
              ? "All work orders (no WO filter)"
              : `${selected.length} work order${selected.length !== 1 ? "s" : ""} selected — click to change`}
          </span>
          <Icon
            d={ICONS.chevronDown}
            size={14}
            className={cls(
              "text-slate-500 transition-transform shrink-0 ml-2",
              open ? "rotate-180" : "",
            )}
          />
        </button>

        {open && (
          <div className="absolute top-full left-0 right-0 z-30 mt-1 bg-slate-800 border border-slate-600 rounded-xl shadow-2xl max-h-56 overflow-y-auto">
            <div className="p-2 space-y-0.5">
              {/* "All WOs" option */}
              <button
                onClick={() => {
                  onChange([]);
                  setOpen(false);
                }}
                className={cls(
                  "w-full text-left px-3 py-2 text-xs rounded-lg transition-colors flex items-center gap-2",
                  selectedIds.length === 0
                    ? "bg-orange-500/15 text-orange-300"
                    : "text-slate-400 hover:bg-slate-700",
                )}
              >
                {selectedIds.length === 0 ? (
                  <Icon d={ICONS.check} size={12} className="text-orange-400" />
                ) : (
                  <span className="w-3" />
                )}
                <span>All work orders (no WO filter)</span>
              </button>

              {workOrders.map((wo) => {
                const isSelected = selectedIds.includes(wo._id);
                return (
                  <button
                    key={wo._id}
                    onClick={() => toggle(wo._id)}
                    className={cls(
                      "w-full text-left px-3 py-2 text-xs rounded-lg transition-colors flex items-center gap-2",
                      isSelected
                        ? "bg-orange-500/15 text-orange-300"
                        : "text-slate-400 hover:bg-slate-700",
                    )}
                  >
                    {isSelected ? (
                      <Icon
                        d={ICONS.check}
                        size={12}
                        className="text-orange-400"
                      />
                    ) : (
                      <span className="w-3" />
                    )}
                    <span>
                      <span className="font-mono">{wo.woNumber}</span>
                      {" — "}
                      {wo.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Selected pills ── */}
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((wo) => (
            <span
              key={wo._id}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-[11px] font-medium rounded-lg border border-orange-500/50 bg-orange-500/10 text-orange-300"
            >
              <span className="font-mono font-semibold">{wo.woNumber}</span>
              <span className="text-orange-400/70">—</span>
              <span className="truncate max-w-[120px]">{wo.title}</span>
              <button
                onClick={() => toggle(wo._id)}
                className="ml-0.5 text-orange-500 hover:text-rose-400 transition-colors rounded shrink-0"
                title={`Remove ${wo.woNumber}`}
              >
                <Icon d={ICONS.x} size={10} />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-slate-600 italic px-1">
          No WO filter — this module will appear for all work orders.
        </p>
      )}
    </div>
  );
}

// ── UOM Picker ─────────────────────────────────────────────────────────────
function UOMPicker({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {UOM_OPTIONS.map((u) => (
        <button
          key={u}
          onClick={() => onChange(u)}
          className={cls(
            "flex-1 py-1.5 text-[11px] font-bold rounded-lg border transition-all font-mono",
            value === u
              ? "border-amber-500 bg-amber-500/15 text-amber-300"
              : "border-slate-600 text-slate-500 hover:border-slate-500 hover:text-slate-300 bg-slate-700/40",
          )}
        >
          {u}
        </button>
      ))}
    </div>
  );
}

// ── Dimension input with live parse preview ────────────────────────────────
function DimInput({ value, onChange, placeholder, parseResult, unit }) {
  const hasVal = value && value.trim();
  const ok = hasVal && parseResult !== null;
  const bad = hasVal && parseResult === null;
  return (
    <div className="space-y-1">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cls(
          "w-full px-2 py-1.5 text-[11px] bg-slate-700 border rounded font-mono placeholder-slate-600 focus:outline-none",
          ok
            ? "border-emerald-600/60 text-emerald-300 focus:border-emerald-500"
            : bad
              ? "border-amber-600/60 text-amber-300 focus:border-amber-500"
              : "border-slate-600 text-slate-200 focus:border-amber-500",
        )}
      />
      {hasVal && (
        <p
          className={cls(
            "text-[9px] font-mono",
            ok ? "text-emerald-500" : "text-amber-600",
          )}
        >
          {ok
            ? `→ ${parseResult.toFixed(4)} ${unit}`
            : "⚠ Can't parse — try 10′  or  10′ 1-1/4″  or  119-1/2″"}
        </p>
      )}
    </div>
  );
}

function ItemRow({
  item,
  idx,
  onChange,
  onRemove,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  token,
  costCode,
}) {
  const [collapsed, setCollapsed] = useState(item._collapsed ?? false);

  const labor = item.labor;
  const dimMode = getDimMode(labor?.uom || "");
  const laborRate = labor?.unitRate || 0;

  const set = (path, value) => onChange(idx, path, value);

  const lfParsed =
    dimMode === "lf" ? parseInches(labor?.defaultLengthInput || "") : null;
  const lfInches = lfParsed;
  const lfFeet = lfInches !== null ? inchesToFeet(lfInches) : 0;

  const sfHParsed =
    dimMode === "sf" ? parseMixedFeet(labor?.defaultHeightInput || "") : null;
  const sfLParsed =
    dimMode === "sf" ? parseMixedFeet(labor?.defaultLengthFtInput || "") : null;

  const handleLFInput = (raw) => {
    set("labor.defaultLengthInput", raw);
    const parsed = parseInches(raw);
    if (parsed !== null)
      set("labor.defaultLengthInches", parseFloat(parsed.toFixed(4)));
  };
  const handleSFHeight = (raw) => {
    set("labor.defaultHeightInput", raw);
    const parsed = parseMixedFeet(raw);
    if (parsed !== null)
      set("labor.defaultHeightFt", parseFloat(parsed.toFixed(4)));
  };
  const handleSFLength = (raw) => {
    set("labor.defaultLengthFtInput", raw);
    const parsed = parseMixedFeet(raw);
    if (parsed !== null)
      set("labor.defaultLengthFt", parseFloat(parsed.toFixed(4)));
  };

  const summaryDesc = labor?.description || "Unnamed item";
  const summaryUom = labor?.uom || "";

  const defaultQty = parseFloat(labor?.defaultQty) || 0;
  const coverageSummary = useMemo(() => {
    if (dimMode === "lf" && lfInches !== null && defaultQty > 0)
      return `${defaultQty} × ${lfInches.toFixed(4)}" = ${(defaultQty * lfFeet).toFixed(4)} LF`;
    if (dimMode === "lf" && lfInches !== null)
      return `${lfInches.toFixed(4)}" default`;
    if (
      dimMode === "sf" &&
      sfHParsed !== null &&
      sfLParsed !== null &&
      defaultQty > 0
    )
      return `${defaultQty} × ${sfHParsed.toFixed(4)}ft × ${sfLParsed.toFixed(4)}ft = ${(defaultQty * sfHParsed * sfLParsed).toFixed(4)} SF`;
    if (dimMode === "sf" && sfHParsed !== null && sfLParsed !== null)
      return `${sfHParsed.toFixed(4)}ft × ${sfLParsed.toFixed(4)}ft`;
    if (dimMode === "qty" && defaultQty > 0)
      return `Default qty: ${defaultQty}`;
    return null;
  }, [dimMode, lfInches, lfFeet, sfHParsed, sfLParsed, defaultQty]);

  const isComplete = !!(labor?.itemCode && labor?.unitRate > 0);

  return (
    <div
      className={cls(
        "rounded-xl border transition-all duration-200",
        item.isActive
          ? "border-sky-700/60 bg-slate-900/90"
          : "border-slate-700/20 bg-slate-950/60 opacity-40",
      )}
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-2 px-3 py-2.5 rounded-t-xl">
        <span className="text-slate-500 text-xs font-mono w-5 text-center select-none shrink-0">
          {idx + 1}
        </span>
        <span
          className={cls(
            "w-1.5 h-1.5 rounded-full shrink-0",
            isComplete ? "bg-sky-400" : "bg-amber-500 animate-pulse",
          )}
        />

        {collapsed ? (
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="text-xs text-white truncate font-semibold">
              {summaryDesc}
            </span>
            {summaryUom && (
              <span className="text-[9px] font-mono bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded shrink-0 border border-slate-700">
                {summaryUom}
              </span>
            )}
            {coverageSummary && (
              <span className="text-[9px] font-mono text-sky-300 bg-sky-900/40 border border-sky-700/50 px-1.5 py-0.5 rounded shrink-0">
                {coverageSummary}
              </span>
            )}
          </div>
        ) : (
          <div className="flex-1 min-w-0">
            {!isComplete && (
              <span className="text-[10px] text-amber-400">
                Incomplete — fill in fields below
              </span>
            )}
            {isComplete && (
              <span className="text-[10px] text-slate-400">Configured</span>
            )}
          </div>
        )}

        {collapsed && laborRate > 0 && (
          <span className="text-[11px] font-mono text-emerald-300 font-bold shrink-0">
            ${fmt(laborRate)}
          </span>
        )}

        <div className="flex items-center gap-1 shrink-0 ml-1">
          <div className="flex flex-col gap-0.5">
            <button
              onClick={() => onMoveUp()}
              disabled={isFirst}
              className="p-0.5 text-slate-500 hover:text-slate-200 disabled:opacity-20 disabled:cursor-not-allowed transition-colors rounded"
              title="Move up"
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                />
              </svg>
            </button>
            <button
              onClick={() => onMoveDown()}
              disabled={isLast}
              className="p-0.5 text-slate-500 hover:text-slate-200 disabled:opacity-20 disabled:cursor-not-allowed transition-colors rounded"
              title="Move down"
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>
          </div>
          <button
            onClick={() => onChange(idx, "isActive", !item.isActive)}
            className={cls(
              "text-[9px] font-bold px-1.5 py-0.5 rounded border transition-colors",
              item.isActive
                ? "text-emerald-300 border-emerald-700/70 bg-emerald-950 hover:bg-emerald-900"
                : "text-slate-400 border-slate-600 bg-slate-800 hover:border-slate-500",
            )}
          >
            {item.isActive ? "ON" : "OFF"}
          </button>
          <button
            onClick={() => onDuplicate(idx)}
            className="p-1 text-sky-500 hover:text-sky-300 transition-colors rounded"
            title="Duplicate"
          >
            <Icon d={ICONS.copy} size={13} />
          </button>
          <button
            onClick={() => onRemove(idx)}
            className="p-1 text-rose-600 hover:text-rose-400 transition-colors rounded"
            title="Remove"
          >
            <Icon d={ICONS.trash} size={13} />
          </button>
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="p-1 text-slate-400 hover:text-slate-200 transition-colors rounded"
          >
            <Icon
              d={ICONS.chevronDown}
              size={14}
              className={cls(
                "transition-transform duration-200",
                collapsed ? "" : "rotate-180",
              )}
            />
          </button>
        </div>
      </div>

      {/* ── Expanded body ── */}
      {!collapsed && (
        <div className="p-3 space-y-3 border-t border-slate-600/60 bg-slate-950/40 rounded-b-xl">
          <div className="bg-slate-950/70 border border-sky-700/40 rounded-lg p-3 space-y-2.5">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold text-sky-300 uppercase tracking-widest">
                Labor Item
              </p>
            </div>
            {!labor?.itemCode ? (
              <LaborSearch
                token={token}
                sectionCode={costCode?.code || ""}
                onSelect={(laborItem) =>
                  onChange(idx, "_attachLabor", laborItem)
                }
                autoFocus
              />
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-900 border border-sky-700/50 rounded-lg">
                <span className="text-[11px] font-mono font-semibold text-sky-300 shrink-0">
                  {labor.itemCode}
                </span>
                <span className="text-[11px] text-slate-200 truncate flex-1">
                  {labor.description}
                </span>
                <button
                  onClick={() => {
                    set("labor.itemCode", "");
                    set("labor.description", "");
                    set("labor.unitRate", 0);
                    set("labor._rateInput", "");
                    set("labor.uom", "EA");
                  }}
                  className="text-[10px] text-slate-500 hover:text-rose-400 border border-slate-600 hover:border-rose-600 px-2 py-0.5 rounded transition-colors shrink-0"
                  title="Remove and select a different item"
                >
                  Change
                </button>
              </div>
            )}

            <div className="grid grid-cols-[90px_1fr] gap-2">
              <div>
                <p className="text-[9px] text-slate-400 mb-1">Item Code</p>
                <input
                  value={labor?.itemCode || ""}
                  readOnly
                  placeholder="L-001"
                  className="w-full px-2 py-1.5 text-[11px] bg-slate-800 border border-slate-600 rounded font-mono text-sky-300 placeholder-slate-600 cursor-not-allowed select-none"
                />
              </div>
              <div>
                <p className="text-[9px] text-slate-400 mb-1">Description</p>
                <input
                  value={labor?.description || ""}
                  onChange={(e) => set("labor.description", e.target.value)}
                  placeholder="e.g. Install Light Gauge Stud"
                  className="w-full px-2 py-1.5 text-[11px] bg-slate-800 border border-slate-600 rounded text-slate-100 placeholder-slate-600 focus:border-sky-500 focus:outline-none"
                />
              </div>
            </div>

            {/* UOM */}
            <div>
              <p className="text-[9px] text-slate-400 mb-1">Unit of Measure</p>
              <UOMPicker
                value={labor?.uom || "EA"}
                onChange={(u) => set("labor.uom", u)}
              />
            </div>

            {/* Default Qty */}
            <div>
              <p className="text-[9px] text-slate-400 mb-1">
                Default Qty{" "}
                <span className="text-slate-600 font-normal normal-case">
                  {dimMode === "lf"
                    ? "(number of pieces at this length)"
                    : dimMode === "sf"
                      ? "(number of panels at these dimensions)"
                      : "(pre-filled qty shown to workers)"}
                </span>
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={labor?.defaultQty ?? ""}
                  onChange={(e) => set("labor.defaultQty", e.target.value)}
                  placeholder="0"
                  className="w-24 px-2 py-1.5 text-[11px] bg-slate-800 border border-slate-600 rounded font-mono text-violet-300 placeholder-slate-600 focus:border-sky-500 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                {dimMode === "lf" &&
                  lfInches !== null &&
                  parseFloat(labor?.defaultQty) > 0 && (
                    <span className="text-[10px] font-mono text-emerald-400">
                      = {(parseFloat(labor.defaultQty) * lfFeet).toFixed(4)} LF
                      total
                    </span>
                  )}
                {dimMode === "sf" &&
                  sfHParsed !== null &&
                  sfLParsed !== null &&
                  parseFloat(labor?.defaultQty) > 0 && (
                    <span className="text-[10px] font-mono text-emerald-400">
                      ={" "}
                      {(
                        parseFloat(labor.defaultQty) *
                        sfHParsed *
                        sfLParsed
                      ).toFixed(4)}{" "}
                      SF total
                    </span>
                  )}
                {dimMode === "qty" &&
                  parseFloat(labor?.defaultQty) > 0 &&
                  laborRate > 0 && (
                    <span className="text-[10px] font-mono text-emerald-400">
                      = ${fmt(parseFloat(labor.defaultQty) * laborRate)}
                    </span>
                  )}
              </div>
            </div>

            {/* LF default length */}
            {dimMode === "lf" && (
              <div>
                <p className="text-[9px] text-slate-400 mb-1">
                  Default Length{" "}
                  <span className="text-slate-600 font-normal normal-case">
                    (per piece — workers confirm)
                  </span>
                </p>
                <DimInput
                  value={labor?.defaultLengthInput || ""}
                  onChange={handleLFInput}
                  placeholder="e.g. 10′  or  10′ 1-1/4″  or  119-1/2″"
                  parseResult={lfInches}
                  unit="inches"
                />
                {lfInches !== null && (
                  <div className="mt-1.5 flex items-center gap-3 px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded-lg">
                    <span className="text-[9px] text-slate-400 uppercase tracking-wide">
                      Converts to
                    </span>
                    <span className="text-[10px] font-mono text-emerald-300">
                      {lfFeet.toFixed(4)} ft
                    </span>
                    <span className="text-[9px] text-slate-500">per piece</span>
                    {parseFloat(labor?.defaultQty) > 0 && (
                      <>
                        <span className="text-[9px] text-slate-500">·</span>
                        <span className="text-[9px] text-emerald-400 font-mono">
                          {parseFloat(labor.defaultQty)} × {lfFeet.toFixed(4)} ={" "}
                          {(parseFloat(labor.defaultQty) * lfFeet).toFixed(4)}{" "}
                          LF
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* SF default dimensions */}
            {dimMode === "sf" && (
              <div className="space-y-2">
                <p className="text-[9px] text-slate-400">
                  Default Dimensions{" "}
                  <span className="text-slate-600 font-normal normal-case">
                    (pre-fills worker form)
                  </span>
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-[9px] text-slate-500 mb-1">Height</p>
                    <DimInput
                      value={labor?.defaultHeightInput || ""}
                      onChange={handleSFHeight}
                      placeholder="e.g. 9' or 10.5'"
                      parseResult={sfHParsed}
                      unit="ft"
                    />
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-500 mb-1">Length</p>
                    <DimInput
                      value={labor?.defaultLengthFtInput || ""}
                      onChange={handleSFLength}
                      placeholder="e.g. 4' or 12'"
                      parseResult={sfLParsed}
                      unit="ft"
                    />
                  </div>
                </div>
                {sfHParsed !== null && sfLParsed !== null && (
                  <div className="flex items-center gap-3 px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded-lg">
                    <span className="text-[9px] text-slate-400 uppercase tracking-wide">
                      Coverage
                    </span>
                    <span className="text-[10px] font-mono text-slate-300">
                      {sfHParsed.toFixed(4)} ft × {sfLParsed.toFixed(4)} ft
                    </span>
                    <span className="text-[9px] text-slate-500">=</span>
                    <span className="text-[10px] font-mono text-emerald-300">
                      {(sfHParsed * sfLParsed).toFixed(4)} SF / piece
                    </span>
                    {parseFloat(labor?.defaultQty) > 0 && (
                      <>
                        <span className="text-[9px] text-slate-500">·</span>
                        <span className="text-[9px] text-emerald-400 font-mono">
                          {parseFloat(labor.defaultQty)} pcs ={" "}
                          {(
                            parseFloat(labor.defaultQty) *
                            sfHParsed *
                            sfLParsed
                          ).toFixed(4)}{" "}
                          SF
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {dimMode === "qty" && (
              <p className="text-[10px] text-slate-500 italic">
                EA — workers enter quantity only, no dimension needed.
              </p>
            )}

            {/* Rate */}
            <div>
              <p className="text-[9px] text-slate-400 mb-1">
                Worker Pay Rate $
              </p>
              <input
                type="text"
                inputMode="decimal"
                value={
                  labor?._rateInput ??
                  (labor?.unitRate > 0 ? String(labor.unitRate) : "")
                }
                onChange={(e) => {
                  const raw = e.target.value;
                  set("labor._rateInput", raw);
                  const parsed = parseFloat(raw);
                  if (!isNaN(parsed)) set("labor.unitRate", parsed);
                }}
                onBlur={(e) => {
                  const parsed = parseFloat(e.target.value);
                  if (!isNaN(parsed)) {
                    set("labor._rateInput", parsed.toFixed(6));
                    set("labor.unitRate", parsed);
                  } else {
                    set("labor._rateInput", "");
                    set("labor.unitRate", 0);
                  }
                }}
                placeholder="0.0000"
                className="w-32 px-2 py-1.5 text-[11px] bg-slate-800 border border-slate-600 rounded font-mono text-emerald-300 placeholder-slate-600 focus:border-sky-500 focus:outline-none"
              />
            </div>
          </div>

          {isComplete && (
            <button
              onClick={() => setCollapsed(true)}
              className="w-full py-1.5 text-[11px] text-slate-500 hover:text-slate-300 border border-dashed border-slate-700/50 rounded-lg transition-colors"
            >
              ↑ Collapse
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════
export default function ModuleBuilderPage() {
  const navigate = useNavigate();
  const { moduleId } = useParams();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const routeState = location.state || {};
  const projectId = routeState.projectId || "";
  // returnTo: caller can pass a specific path to go back to
  const returnTo = routeState.returnTo || null;
  const isEdit = !!moduleId;
  const returnTab = routeState.returnTab || null;
  // ── Form state ──────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [costCode, setCostCode] = useState({ id: null, code: "", name: "" });
  // Worker-facing display name for the section (overrides costCode.name in mobile UI)
  const [sectionDisplayName, setSectionDisplayName] = useState("");
  const [description, setDesc] = useState("");
  // Multi-select for new modules; single string for edit (module has one WO)
  const [selectedWOIds, setSelectedWOIds] = useState([]); // new module

  const [isActive, setIsActive] = useState(true);
  const [items, setItems] = useState([]);
  const [workOrders, setWorkOrders] = useState(routeState.workOrders || []);
  const [burdenPct, setBurdenPct] = useState(0);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [collapseAll, setCollapseAll] = useState(false);
  const isDirty = useRef(false);
  const addAnchorRef = useRef(null);

  // ── Back navigation ──────────────────────────────────────────────────────
  const handleBack = () => {
    const dest =
      returnTo ||
      (projectId ? `/Projects/${projectId}?tab=Site+Requests` : null);
    if (dest) {
      navigate(dest, { state: { returnTab: returnTab || "modules" } });
    } else {
      navigate(-1);
    }
  };

  // Load work orders if not passed via route state
  useEffect(() => {
    if (workOrders.length || !projectId) return;
    axios
      .get(`${API}/workorders3/project/${projectId}`, { headers })
      .then((r) =>
        setWorkOrders(
          (r.data.workOrders || []).filter((w) =>
            ["piecework", "union_piecework"].includes(w.type),
          ),
        ),
      )
      .catch(() => {});
  }, [projectId]);

  // Load module for edit
  // Load module for edit
  useEffect(() => {
    if (!isEdit) return;

    // Reset state + show loading spinner every time moduleId changes,
    // so stale data from a previously-loaded module never lingers on screen.
    setLoading(true);
    setError("");
    setName("");
    setCostCode({ id: null, code: "", name: "" });
    setSectionDisplayName("");
    setDesc("");
    setIsActive(true);
    setSelectedWOIds([]);
    setItems([]);
    isDirty.current = false;

    axios
      .get(`${API}/pieceworkRequests/module-admin/list`, {
        headers,
        params: { moduleId },
      })
      .then((r) => {
        const mod = (r.data.modules || []).find((m) => m._id === moduleId);
        if (!mod) {
          setError("Module not found.");
          return;
        }
        setName(mod.name || "");
        setCostCode(
          mod.costCode?.id ? mod.costCode : { id: null, code: "", name: "" },
        );
        setSectionDisplayName(mod.sectionDisplayName || "");
        setDesc(mod.description || "");
        setIsActive(mod.isActive !== false);

        // Normalise: support both legacy workOrder (single) and new workOrders (array)
        const woIds = mod.workOrders?.length
          ? mod.workOrders.map((w) => (typeof w === "object" ? w._id : w))
          : mod.workOrder
            ? [
                typeof mod.workOrder === "object"
                  ? mod.workOrder._id
                  : mod.workOrder,
              ]
            : [];
        setSelectedWOIds(woIds);

        const rawItems = mod.pairedItems || mod.items || [];
        setItems(
          rawItems.map((p) => {
            const labor = p.labor || {};
            const mat = p.material || {};
            return {
              ...p,
              suggestedQty: p.suggestedQty || 0,
              _localId: Math.random().toString(36).slice(2),
              _collapsed: true,
              labor: {
                itemCode: labor.itemCode || "",
                description: labor.description || "",
                uom: labor.uom || mat.uom || "EA",
                defaultQty: labor.defaultQty ?? "",
                unitRate: labor.unitRate || 0,
                defaultLengthInches:
                  labor.defaultLengthInches || mat.defaultLengthInches || 0,
                defaultLengthInput:
                  labor.defaultLengthInput ||
                  mat.defaultLengthInput ||
                  (mat.defaultLengthInches
                    ? String(mat.defaultLengthInches) + '"'
                    : ""),
                defaultHeightFt:
                  labor.defaultHeightFt || mat.defaultHeightFt || 0,
                defaultLengthFt:
                  labor.defaultLengthFt || mat.defaultLengthFt || 0,
                defaultHeightInput:
                  labor.defaultHeightInput ||
                  mat.defaultHeightInput ||
                  (mat.defaultHeightFt
                    ? String(mat.defaultHeightFt) + "'"
                    : ""),
                defaultLengthFtInput:
                  labor.defaultLengthFtInput ||
                  mat.defaultLengthFtInput ||
                  (mat.defaultLengthFt
                    ? String(mat.defaultLengthFt) + "'"
                    : ""),
              },
            };
          }),
        );
      })
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [moduleId]);

  // Burden pct — for edit mode use the single workOrderId; for new use first selected
  const burdenWOId = selectedWOIds[0] || "";

  useEffect(() => {
    if (!burdenWOId || !token) {
      setBurdenPct(0);
      return;
    }
    const wo = workOrders.find((w) => w._id === burdenWOId);
    if (!wo?.union || wo.union === "non-union") {
      setBurdenPct(0);
      return;
    }
    axios
      .get(`${API}/pieceworkerLogs3/burden-rate`, {
        headers,
        params: {
          workOrderId: burdenWOId,
          workerId: "000000000000000000000000",
        },
      })
      .then((r) => setBurdenPct(r.data?.burdenPct || 0))
      .catch(() => setBurdenPct(0));
  }, [burdenWOId, workOrders, token]);

  // ── Item mutation helpers ──────────────────────────────────────────────
  const updateItem = useCallback((idx, path, value) => {
    isDirty.current = true;
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        if (path === "_attachLabor") {
          const rate = value.defaultCost || 0;
          return {
            ...item,
            labor: {
              ...item.labor,
              itemCode: value.itemCode || "",
              description: value.description || value.itemName || "",
              uom: value.uom || item.labor?.uom || "EA",
              unitRate: rate,
              _rateInput: rate > 0 ? String(rate) : "",
            },
          };
        }
        if (path === "isActive") return { ...item, isActive: value };
        if (!path.includes(".")) return { ...item, [path]: value };
        const [top, sub] = path.split(".");
        const updated = {
          ...item,
          [top]: { ...(item[top] || {}), [sub]: value },
        };

        return updated;
      }),
    );
  }, []); // ← was missing entirely

  const removeItem = useCallback((idx) => {
    isDirty.current = true;
    setItems((p) => p.filter((_, i) => i !== idx));
  }, []);

  const moveItem = useCallback((idx, direction) => {
    isDirty.current = true;
    setItems((prev) => {
      const next = [...prev];
      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= next.length) return prev;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      // Keep sortOrder in sync with position
      return next.map((item, i) => ({ ...item, sortOrder: i }));
    });
  }, []);

  const duplicateItem = useCallback((idx) => {
    isDirty.current = true;
    setItems((prev) => {
      const copy = {
        ...prev[idx],
        _localId: Math.random().toString(36).slice(2),
        _collapsed: false,
      };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  }, []);

  const addItem = useCallback(() => {
    isDirty.current = true;
    setItems((p) => [
      ...p,
      newItem({
        labor: emptyLabor(),
        suggestedQty: 0,
        sortOrder: p.length,
        _collapsed: false,
      }),
    ]);
    setTimeout(
      () =>
        addAnchorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        }),
      100,
    );
  }, []);

  const toggleCollapseAll = () => {
    const next = !collapseAll;
    setCollapseAll(next);
    setItems((p) => p.map((i) => ({ ...i, _collapsed: next })));
  };

  const applyBulkEdit = useCallback((updated) => {
    isDirty.current = true;
    setItems(updated);
  }, []);

  // ── Build save payload ─────────────────────────────────────────────────
  const buildSaveBody = (overrides = {}) => ({
    name: name.trim(),
    costCode: {
      id: costCode.id || null,
      code: costCode.code || "",
      name: costCode.name || "",
    },
    sectionDisplayName: sectionDisplayName.trim(),
    description,
    projectId: projectId || null,
    isActive,
    workOrderIds: selectedWOIds,
    pairedItems: items.map((p, i) => ({
      material: null,
      labor: {
        itemCode: p.labor?.itemCode || "",
        description: p.labor?.description || "",
        uom: p.labor?.uom || "EA",
        unitRate: p.labor?.unitRate || 0,
        defaultQty: p.labor?.defaultQty ?? "",
        defaultLengthInches: p.labor?.defaultLengthInches || 0,
        defaultLengthInput: p.labor?.defaultLengthInput || "",
        defaultHeightFt: p.labor?.defaultHeightFt || 0,
        defaultLengthFt: p.labor?.defaultLengthFt || 0,
        defaultHeightInput: p.labor?.defaultHeightInput || "",
        defaultLengthFtInput: p.labor?.defaultLengthFtInput || "",
      },
      suggestedQty: typeof p.suggestedQty === "number" ? p.suggestedQty : 0,
      isActive: p.isActive !== false,
      sortOrder: i,
    })),
    ...overrides,
  });

  // ── Save ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError("");
    if (!name.trim()) {
      setError("Module name is required.");
      return;
    }
    if (!costCode?.id) {
      setError("Section (cost code) is required.");
      return;
    }
    if (items.length === 0) {
      setError("Add at least one item.");
      return;
    }
    const incomplete = items.find(
      (p) => !p.labor?.itemCode?.trim() || !(p.labor?.unitRate > 0),
    );
    if (incomplete) {
      setError(
        `"${incomplete.labor?.description || "An item"}" must have an item code and rate.`,
      );
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const body = buildSaveBody();
        await axios.put(
          `${API}/pieceworkRequests/module-admin/${moduleId}`,
          body,
          { headers },
        );
        isDirty.current = false;
        setSuccessMsg("Module updated.");
        // Stay on page — clear banner after 3 s
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        const body = buildSaveBody();
        await axios.post(`${API}/pieceworkRequests/module-admin`, body, {
          headers,
        });
        isDirty.current = false;
        setSuccessMsg("Module created.");
        // Navigate back after a short pause so the user sees the confirmation
        setTimeout(() => handleBack(), 900);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Derived counts ─────────────────────────────────────────────────────
  const incompleteCount = items.filter(
    (p) => !p.labor?.itemCode?.trim() || !(p.labor?.unitRate > 0),
  ).length;
  const suggestedQtyCount = items.filter(
    (p) => (p.suggestedQty || 0) > 0,
  ).length;

  if (loading)
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={28} />
        <span className="ml-3 text-slate-400">Loading module…</span>
      </div>
    );

  return (
    <>
      <div className="mx-auto px-4 py-6 space-y-5 pb-32">
        {/* ── Header ── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 text-slate-500 hover:text-slate-300 bg-slate-700 border border-slate-600 rounded-lg transition-colors"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-base font-bold text-slate-100">
                {isEdit ? "Edit Module" : "New Module"}
              </h1>
              <p className="text-[11px] text-slate-500">
                Cost-code sections · Labor items with coverage · Section-aware
                search
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold text-white bg-orange-500 rounded-lg hover:bg-orange-400 disabled:opacity-40 transition-colors"
            >
              {saving ? (
                <>
                  <Spinner size={13} />
                  Saving…
                </>
              ) : isEdit ? (
                "Save Changes"
              ) : selectedWOIds.length > 1 ? (
                `Create ${selectedWOIds.length} Modules`
              ) : (
                "Create Module"
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="px-4 py-3 bg-rose-950/50 border border-rose-700/50 rounded-xl text-sm text-rose-300 flex items-start gap-2">
            <span className="shrink-0 mt-0.5">⚠</span>
            {error}
          </div>
        )}
        {successMsg && (
          <div className="px-4 py-3 bg-emerald-950/50 border border-emerald-700/50 rounded-xl text-sm text-emerald-300">
            ✓ {successMsg}
          </div>
        )}

        {/* ── Module Details ── */}
        <div className="bg-slate-900 rounded-xl border border-slate-700/80 p-5 space-y-4">
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            Module Details
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wide block mb-1.5">
                Module Name *
              </label>
              <input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  isDirty.current = true;
                }}
                placeholder="e.g. Unit Drywall Package"
                className="w-full px-3 py-2 text-sm bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-orange-500 focus:outline-none"
              />
            </div>

            {/* WO picker — multi-select for new, single for edit */}
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wide block mb-1.5">
                Work Orders{" "}
                <span className="text-slate-600 font-normal normal-case">
                  (optional — leave blank to apply to all)
                </span>
                {selectedWOIds.length > 1 && (
                  <span className="ml-2 text-orange-400 normal-case font-normal">
                    → linked to {selectedWOIds.length} work orders
                  </span>
                )}
              </label>
              <WorkOrderMultiSelect
                workOrders={workOrders}
                selectedIds={selectedWOIds}
                onChange={(ids) => {
                  setSelectedWOIds(ids);
                  isDirty.current = true;
                }}
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide block mb-1.5">
                Work Section *
              </label>
              <CostCodeSelector
                token={token}
                value={costCode}
                onChange={(v) => {
                  setCostCode(v);
                  isDirty.current = true;
                }}
              />
            </div>

            {/* Worker-facing display name */}
            <div className="sm:col-span-2">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide block mb-1.5">
                Section Display Name{" "}
                <span className="text-slate-600 font-normal normal-case">
                  (worker-facing label — overrides cost code name in mobile app)
                </span>
              </label>
              <input
                value={sectionDisplayName}
                onChange={(e) => {
                  setSectionDisplayName(e.target.value);
                  isDirty.current = true;
                }}
                placeholder={
                  costCode?.name
                    ? `e.g. "${costCode.name}" or a friendlier label like "Stud Framing"`
                    : "e.g. Stud Framing, Drywall Hang, etc."
                }
                className="w-full px-3 py-2 text-sm bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-sky-500 focus:outline-none"
              />
              {sectionDisplayName && (
                <p className="text-[10px] text-sky-400 mt-1">
                  Workers see:{" "}
                  <span className="font-semibold">"{sectionDisplayName}"</span>
                </p>
              )}
            </div>

            <div className="sm:col-span-2">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide block mb-1.5">
                Description
              </label>
              <input
                value={description}
                onChange={(e) => {
                  setDesc(e.target.value);
                  isDirty.current = true;
                }}
                placeholder="Optional notes…"
                className="w-full px-3 py-2 text-sm bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={() => setIsActive((p) => !p)}
              className={cls(
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors border",
                isActive
                  ? "bg-orange-500/10 border-orange-500 text-orange-400"
                  : "bg-slate-700 border-slate-600 text-slate-400 hover:border-slate-500",
              )}
            >
              <span
                className={cls(
                  "w-2 h-2 rounded-full",
                  isActive ? "bg-orange-500" : "bg-slate-500",
                )}
              />
              {isActive
                ? "Active — visible to workers"
                : "Inactive — hidden from workers"}
            </button>
          </div>
        </div>

        {/* ── Items ── */}
        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-bold text-gray-700">Items</h2>
              <div className="flex items-center gap-2 text-[10px] flex-wrap">
                {items.length > 0 && (
                  <span className="px-2 py-0.5 bg-sky-500/10 text-bg-400 border border-sky-800/40 rounded-full">
                    {items.length} labor item{items.length !== 1 ? "s" : ""}
                  </span>
                )}
                {incompleteCount > 0 && (
                  <span className="px-2 py-0.5 bg-amber-500/10 text-amber-400 border border-amber-700/40 rounded-full">
                    ⚠ {incompleteCount} incomplete
                  </span>
                )}
                {suggestedQtyCount > 0 && (
                  <span className="px-2 py-0.5 bg-violet-500/10 text-violet-400 border border-violet-800/40 rounded-full">
                    ~qty on {suggestedQtyCount}
                  </span>
                )}
              </div>
            </div>
            {items.length > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleCollapseAll}
                  className="px-2.5 py-1.5 text-[11px] text-slate-400 bg-slate-700 border border-slate-600 rounded-lg hover:bg-slate-600 transition-colors"
                >
                  {collapseAll ? "Expand All" : "Collapse All"}
                </button>
              </div>
            )}
          </div>

          {items.length > 0 && (
            <div className="space-y-2">
              {items.map((item, idx) => (
                <ItemRow
                  key={item._localId || idx}
                  item={item}
                  idx={idx}
                  onChange={updateItem}
                  onRemove={removeItem}
                  onDuplicate={duplicateItem}
                  onMoveUp={() => moveItem(idx, "up")}
                  onMoveDown={() => moveItem(idx, "down")}
                  isFirst={idx === 0}
                  isLast={idx === items.length - 1}
                  token={token}
                  costCode={costCode}
                />
              ))}
            </div>
          )}

          {items.length === 0 && (
            <div className="bg-slate-800/50 rounded-xl border border-dashed border-slate-600 p-10 text-center">
              <p className="text-sm text-slate-500 mb-1">No items yet</p>
              <p className="text-xs text-slate-600">
                Add labor items below — set UOM to LF or SF to unlock dimension
                defaults
              </p>
            </div>
          )}

          <div ref={addAnchorRef} />

          <button
            onClick={addItem}
            className="w-full flex items-center gap-2.5 px-4 py-3.5 bg-sky-950/20 border-2 border-dashed border-sky-800/50 rounded-xl hover:border-sky-700/70 hover:bg-sky-950/30 transition-all text-left group"
          >
            <div className="w-8 h-8 rounded-lg bg-sky-950/50 border border-sky-800/50 flex items-center justify-center shrink-0 group-hover:bg-sky-950 transition-colors">
              <Icon d={ICONS.plus} size={15} className="text-white" />
            </div>
            <div>
              <p className="text-xs font-bold text-gray-800">Add Labor Item</p>
              <p className="text-[10px] text-sky-800 mt-0.5">
                EA, LF, or SF — dimensions optional
              </p>
            </div>
          </button>
        </div>

        {/* ── Summary bar ── */}
        {items.length > 0 && (
          <div className="bg-slate-900 rounded-xl border border-slate-700/80 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-4 text-xs text-slate-300">
              <span>
                {items.length} item{items.length !== 1 ? "s" : ""} total
              </span>
              {incompleteCount > 0 && (
                <span className="text-amber-400 font-semibold">
                  ⚠ {incompleteCount} need attention
                </span>
              )}
              {suggestedQtyCount > 0 && (
                <span className="text-violet-400">
                  {suggestedQtyCount} with suggested qty
                </span>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold text-white bg-orange-500 rounded-lg hover:bg-orange-400 disabled:opacity-40 transition-colors"
            >
              {saving ? (
                <>
                  <Spinner size={12} />
                  Saving…
                </>
              ) : isEdit ? (
                "Save Changes"
              ) : (
                "Create Module"
              )}
            </button>
          </div>
        )}
      </div>

      {/* ── Mobile bottom bar ── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 border-t border-slate-700 px-4 py-3 flex items-center justify-between gap-3 sm:hidden backdrop-blur-sm">
        <div className="text-xs text-slate-500">
          {items.length} item{items.length !== 1 ? "s" : ""}
          {incompleteCount > 0 && (
            <span className="text-amber-400 ml-2">
              ⚠ {incompleteCount} incomplete
            </span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-5 py-2 text-sm font-bold text-white bg-orange-500 rounded-lg hover:bg-orange-400 disabled:opacity-40 transition-colors"
        >
          {saving ? (
            <>
              <Spinner size={14} />
              Saving…
            </>
          ) : isEdit ? (
            "Save"
          ) : (
            "Create"
          )}
        </button>
      </div>
    </>
  );
}
